"use client";

import React, { useEffect, useRef, useState } from "react";
import TextCarousel from "../ui/textCarousel";
import Image from "next/image";
import gsap from "gsap";
import ScrollTrigger from "gsap/dist/ScrollTrigger";
import BottomCurves from "../curves/bottomCurves";
import BannerFloatingArrow from "../uiDecor/bannerFloatingArrow";
import ArtboardCube from "@/app/assets/cube-artboard.png";
import HumanInsightCube from "@/app/assets/cube-human-insight.png";
import CultureFirstCube from "@/app/assets/cube-culture-first.png";

gsap.registerPlugin(ScrollTrigger);

export default function PinnedSectionBk({ data }: { data: any }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const carouselTextRef = useRef<HTMLDivElement>(null);
  const cubeRef = useRef(null);
  const counterRefs: any = useRef([...Array(3)].map(() => React.createRef()));
  const [carouselHeight, setCarouselHeight] = useState(0);
  const [frame, setFrame] = useState(0);

  function updateCounter(index: number, action: string) {
    counterRefs.current[index].current.childNodes.forEach(
      (child: any, i: number) => {
        if (index === i) {
          if (action === "add") {
            child.classList.add("active");
          }
          if (action === "remove") {
            child.classList.remove("active");
          }
        }
      }
    );
  }

  // cube panel animation
  useEffect(() => {
    const panels = document.querySelectorAll(".panel");

    panels.forEach((panel, index) => {
      const pinSpacing = index === panels.length - 1 ? true : false;
      const panelTl = gsap.timeline({
        scrollTrigger: {
          trigger: panel,
          start: `${window.innerWidth > 969 ? "center center" : "top 18%"
            }`,
          end: `${window.innerWidth > 969 ? "150% center" : "bottom 20%"
            }`,
          pin: true,
          pinSpacing,
          markers: false,
          scrub: 1,
          onEnter: () => {
            panel.classList.add("active");
            panel.classList.remove("opacity-0");
            updateCounter(index, "add");
            setFrame(index);
          },
          onLeave: () => {
            if (index !== panels.length - 1) {
              panel.classList.remove("active");
              panel.classList.add("opacity-0");
              updateCounter(index, "remove");
            }
          },
          onEnterBack: () => {
            panel.classList.add("active");
            panel.classList.remove("opacity-0");
            updateCounter(index, "add");
            setFrame(index);
          },
          onLeaveBack: () => {
            if (index !== 0) {
              panel.classList.remove("active");
              panel.classList.add("opacity-0");
              updateCounter(index, "remove");
            }
          },
        },
      });

      return () => {
        if (panelTl.scrollTrigger) {
          panelTl.scrollTrigger.kill();
        }
      };
    });

    if (carouselTextRef.current) {
      setCarouselHeight(carouselTextRef.current.clientHeight);
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  // cube animation
  useEffect(() => {
    if (
      carouselHeight !== 0 &&
      carouselTextRef.current &&
      containerRef.current
    ) {
      const containerTl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: `${window.innerWidth > 969
            ? "top top"
            : `${carouselTextRef.current.clientHeight + 48
            }px 20%`
            }`,
          end: `${window.innerWidth > 969
            ? "bottom bottom"
            : `${containerRef.current.clientHeight -
            carouselTextRef.current.clientHeight -
            48
            }px bottom`
            }`,
          scrub: 1,
          pin: cubeRef.current,
          pinSpacing: false,
          markers: false,
        },
      });

      return () => {
        if (containerTl.scrollTrigger) {
          containerTl.scrollTrigger.kill();
        }
      };
    }
  }, [carouselHeight]);

  // text carousel animation
  useEffect(() => {
    if (containerRef.current && carouselTextRef.current) {
      const textTl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: `${window.innerWidth > 969 ? "top top" : "top top"}`,
          end: `${window.innerWidth > 969
            ? `${containerRef.current.clientHeight}px top`
            : `${containerRef.current.clientHeight -
            carouselTextRef.current.clientHeight -
            48
            }px top`
            }`,
          scrub: 1,
          pin: carouselTextRef.current,
          pinSpacing: false,
          markers: false,
        },
      });

      return () => {
        if (textTl.scrollTrigger) {
          textTl.scrollTrigger.kill();
        }
      };
    }
  }, []);

  return (
    <div
      id="dna-section"
      className="relative px-0 bg-purpleTurquoise slow-down md:pb-0 pb-8"
      ref={containerRef}
    >
      <div
        ref={carouselTextRef}
        className="flex mb-12 h-[15lvh] relative lg:absolute top-[5vh] left-0 right-0"
      >
        <TextCarousel
          text={data.headline}
          color={"green"}
          colouredPosition={1}
        />
        <BannerFloatingArrow
          direction={"left"}
          className="!-bottom-[450px] left-[3.625vw] md:!-bottom-[630px] lg:!-bottom-[550px] xl:!-bottom-[800px] 2xl:!-bottom-[680px] 3xl:!-bottom-[680px] 4xl:!-bottom-[700px] 5xl:!-bottom-[980px] md:left-[10.625vw]"
          arrowtype="scroll-on"
        />
      </div>

      <div className="flex sm:px-[10.417vw] px-7 flex-col tablet:flex-row relative">
        <div className="panel-holder w-full z-10">
          {data.contents.map((content: any, i: number) => (
            <div
              key={`panel-${i}`}
              className={`panel flex lg:flex-row flex-col gap-5 md:gap-0 ${i !== 0 ? "opacity-0" : "active"
                } md:h-screen h-[85lvh] md:items-center`}
            >
              <div className="flex flex-col md:justify-between lg:w-1/2 w-full">
                <p className=" text-[16px] smallMobile:text-xl font-semibold md:text-font-36 text-white leading-[1.2]">
                  {data.heading}
                </p>
                <div className="leftDescriptionHolder flex flex-row items-center md:gap-[2vw] gap-5 mb-5 lg:mb-0">
                  <div
                    className="counter flex flex-col gap-y-2 gap-x-4 justify-center"
                    ref={counterRefs.current[i]}
                  >
                    {data.contents.map(
                      (_: any, j: number) => (
                        <span
                          key={`counter-${i}-${j}`}
                          className={`${i == 0 &&
                            j == 0 &&
                            "active"
                            } counterBtn text-[12px] md:text-body-fontsize flex items-center justify-center md:w-10 w-8  h-auto aspect-square border-[1px] border-white rounded-full leading-[1] text-white`}
                        >
                          {j + 1}
                        </span>
                      )
                    )}
                  </div>
                  <div className="inner">
                    <h3 className="title text-[33px] smallMobile:text-4xl font-bold md:text-font-85 text-[#37DED7]">
                      {content.title}
                    </h3>
                    <p className="description max-smallMobile:text-[14px] text-white leading-[1.3] mb-0">
                      {content.description}
                    </p>
                  </div>
                </div>
              </div>

              <div className="lg:w-1/2 md:w-[75%] smallMobile:w-full w-[80%] md:max-h-[unset] max-h-[35lvh] mx-auto panelImage">
                <Image
                  src={ArtboardCube}
                  alt={""}
                  className={`${frame == 0
                    ? "scale-100"
                    : "scale-0 !h-0 !w-0"
                    } duration-200 lg:w-[80%] w-full h-full object-contain mx-auto`}
                />
                <Image
                  src={HumanInsightCube}
                  alt={""}
                  className={`${frame == 1
                    ? "scale-100"
                    : "scale-0 !h-0 !w-0"
                    } duration-200 lg:w-[80%] w-full h-full object-contain mx-auto`}
                />
                <Image
                  src={CultureFirstCube}
                  alt={""}
                  className={`${frame == 2
                    ? "scale-100"
                    : "scale-0 !h-0 !w-0"
                    } duration-200 lg:w-[80%] w-full h-full object-contain mx-auto`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <BottomCurves
        color={data.waveBgColor.waveBgColor}
        direction={"left"}
      />
      <BannerFloatingArrow
        direction={"right"}
        className={`right-[6vw] -top-24 md:-top-36`}
        name="our-dna"
      />
    </div>
  );
}
